import AddIcon from '@mui/icons-material/Add';
import {
  Avatar,
  Button,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import type { User } from '@verantos-dev/core-annotation-tool';

import { getInitials } from './getInitials';

interface AddPersonMenuProps {
  /** Localised button label, e.g. "Add annotator". */
  label: string;
  /** People that can still be added (already-added ones excluded by the parent). */
  options: User[];
  onSelect: (person: User) => void;
  /** When true, the menu shows skeleton rows instead of options. */
  loading?: boolean;
}

const SKELETON_ROW_COUNT = 4;

const SkeletonMenuItems = () => (
  <>
    {Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
      <MenuItem key={index} disableRipple sx={{ pointerEvents: 'none' }}>
        <ListItemAvatar sx={{ minWidth: 40 }} />
        <Skeleton variant="text" width={120} />
      </MenuItem>
    ))}
  </>
);

/**
 * Dashed pill button that opens a dropdown of selectable people (avatar + name).
 * Used in edit mode to add annotators/supervisors.
 */
export const AddPersonMenu = ({
  label,
  options,
  onSelect,
  loading = false,
}: AddPersonMenuProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleSelect = (person: User) => {
    onSelect(person);
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          textTransform: 'none',
          borderRadius: 999,
          borderStyle: 'dashed',
          height: 32,
          px: theme.spacing(2),
        }}
      >
        {label}
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        {loading ? (
          <SkeletonMenuItems />
        ) : options.length === 0 ? (
          <MenuItem disabled>
            {formatMessage({ id: 'admin-common.peopleSection.noPeopleToAdd' })}
          </MenuItem>
        ) : (
          options.map((person) => (
            <MenuItem key={person.id} onClick={() => handleSelect(person)}>
              <ListItemAvatar sx={{ minWidth: 40 }}>
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    fontSize: 12,
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                  }}
                >
                  {getInitials(person.label)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary={person.label} />
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
};
